import { Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import WaveformData from 'waveform-data';

import { Sound } from './audio/Sound';
import OverviewWaveform from './OverviewWaveform';
import Player from './Player';
import SegmentBar from './SegmentBar';
import SelectionBox from './SelectionBox';
import ViewBox from './ViewBox';
import ZoomWaveform from './ZoomWaveform';

export interface WaveformOptions {
  mountPoint: HTMLElement;
  height: number;
  padding: number;
}

interface AudioEditorOptions {
  audioUrl: string;
  waveformUrl?: string;
  cursorColor: string;
  waveformColor: string;
  backgroundColor: string;
  overview: WaveformOptions;
  zoom: WaveformOptions;
  selectionBox: {
    backgroundColor: string;
    waveformColor: string;
  };
  viewBox: {
    backgroundColor: string;
    handlesColor: string;
    colorBound: string;
  };
  segment: {
    from: number;
    to: number;
  };
  intro: {
    at: number;
    color: string;
    isActive: boolean;
  };
  outro: {
    at: number;
    color: string;
    isActive: boolean;
  };
  mix: {
    at: number;
    color: string;
    isActive: boolean;
  };
  axis: {
    backgroundColor: string;
    fontColor: string;
  };
}

export class AudioEditor {
  public unsubscribeAll = new Subject();
  private readonly mixPointChangedSubject = new Subject<SegmentBar>();
  private readonly outroPointChangedSubject = new Subject<SegmentBar>();
  private readonly introPointChangedSubject = new Subject<SegmentBar>();
  private readonly playSubject = new Subject<void>();
  private readonly pauseSubject = new Subject<void>();
  private readonly startSubject = new Subject<number>();
  private readonly endSubject = new Subject<number>();
  private readonly waveformLoadedSubject = new Subject<WaveformData>();
  private readonly waveformReadyToDrawSubject = new Subject<WaveformData>();
  private readonly viewDraggingSubject = new Subject<{ from: number; to: number }>();
  private readonly viewDraggedSubject = new Subject<{ from: number; to: number }>();
  private readonly playerChangedSubject = new Subject<{ currentTime: number }>();
  private readonly playerLoadedSubject = new Subject<{ duration: number }>();
  private readonly waveformZoomSubject = new Subject<{ width: number; zoom: number }>();
  // private duration: number;

  public player = new Player(
    this,
    this.playSubject,
    this.pauseSubject,
    this.playerChangedSubject,
    this.playerLoadedSubject,
  );
  public waveformData: WaveformData = null as any;
  public overviewWaveform = new OverviewWaveform(this, this.viewDraggingSubject);
  public zoomWaveform = new ZoomWaveform(
    this,
    this.viewDraggingSubject,
    this.waveformZoomSubject,
  );
  public selectionBox = new SelectionBox(this, this.startSubject, this.endSubject);
  public viewBox = new ViewBox(
    this,
    this.waveformReadyToDrawSubject,
    this.viewDraggedSubject,
  );
  public segmentBar = new SegmentBar(
    this,
    this.mixPointChangedSubject,
    this.outroPointChangedSubject,
    this.introPointChangedSubject,
  );

  constructor(public options: AudioEditorOptions) {
    this.initialize();
  }

  private async initialize() {
    if (this.options.waveformUrl) {
      try {
        const result = await fetch(this.options.waveformUrl);
        const data = await result.arrayBuffer();
        if (!result || result.status !== 200) {
          throw new Error();
        }
        this.waveformData = WaveformData.create(data);
        this.waveformLoadedSubject.next(this.waveformData);
      } catch (e) {
        await this.initWithoutWaveformCaltulated();
      }
    } else {
      await this.initWithoutWaveformCaltulated();
    }
  }

  private async initWithoutWaveformCaltulated() {
    const response = await fetch(this.options.audioUrl, {
      // mode: 'no-cors', // no-cors, cors, *same-origin
      cache: 'reload',
    });
    const options = {
      audio_context: new AudioContext(),
      array_buffer: await response.arrayBuffer(),
      scale: 256,
    };

    WaveformData.createFromAudio(options, (err, waveform) => {
      if (err) {
        console.error(err, err.message);

        return;
      }
      this.waveformData = waveform;
      this.waveformLoadedSubject.next(this.waveformData);
    });
  }

  onDestroy() {
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
  }

  setPreviousSound(trackObj: object) {
    if (trackObj && trackObj['audioUrl']) {
      const prevSound = new Sound(
        trackObj['audioUrl'],
        0,
        trackObj['extro'] / 1000,
        0,
        true,
        trackObj['fadeOut'] / 1000,
        trackObj['fadeOut'] !== 0,
        trackObj['mix'] / 1000,
        trackObj['mix'] !== 0,
        'previous',
      );
      this.player.setPrevSound(prevSound);

      return;
    }
    this.player.setPrevSound(null);
  }

  setNextSound(trackObj: object) {
    if (trackObj && trackObj['audioUrl']) {
      const nextSound = new Sound(
        trackObj['audioUrl'],
        trackObj['intro'] / 1000,
        trackObj['extro'] / 1000,
        trackObj['fadeIn'] / 1000,
        trackObj['fadeIn'] !== 0,
        trackObj['fadeOut'] / 1000,
        trackObj['fadeOut'] !== 0,
        trackObj['mix'] / 1000,
        trackObj['mix'] !== 0,
        'next',
      );
      this.player.setNextSound(nextSound);

      return;
    }
    this.player.setNextSound(null);
  }

  setEndAtCursor(): void {
    const time = this.player.time;
    this.selectionBox.setEnd(time);
  }

  setStartAtCursor(): void {
    const time = this.player.time;
    this.selectionBox.setStart(time);
  }

  setMixAtCursor(): void {
    const time = this.player.time;
    this.segmentBar.setMixPoint(time);
  }

  setOutroAtCursor(): void {
    const time = this.player.time;
    this.segmentBar.setOutroPoint(time);
  }

  setIntroAtCursor(): void {
    const time = this.player.time;
    this.segmentBar.setIntroPoint(time);
  }

  get mixPointChanged$(): Observable<number> {
    return this.mixPointChangedSubject.asObservable().pipe(
      takeUntil(this.unsubscribeAll),
      map(val => val.mixPoint),
    );
  }
  get outroPointChanged$(): Observable<number> {
    return this.outroPointChangedSubject.asObservable().pipe(
      takeUntil(this.unsubscribeAll),
      map(val => val.outroPoint),
    );
  }
  get introPointChanged$(): Observable<number> {
    return this.introPointChangedSubject.asObservable().pipe(
      takeUntil(this.unsubscribeAll),
      map(val => val.introPoint),
    );
  }

  get pauseChanged$(): Observable<void> {
    return this.pauseSubject.pipe(takeUntil(this.unsubscribeAll));
  }

  get playChanged$(): Observable<void> {
    return this.playSubject.pipe(takeUntil(this.unsubscribeAll));
  }

  get onWaveformLoaded$(): Observable<WaveformData> {
    return this.waveformLoadedSubject.pipe(takeUntil(this.unsubscribeAll), take(1));
  }

  get onWaveformReadyToDraw$(): Observable<WaveformData> {
    return this.waveformReadyToDrawSubject.pipe(takeUntil(this.unsubscribeAll), take(1));
  }

  get onViewDragging$(): Observable<{ from: number; to: number }> {
    return this.viewDraggingSubject.pipe(takeUntil(this.unsubscribeAll));
  }

  get onViewDragged$(): Observable<{ from: number; to: number }> {
    return this.viewDraggedSubject.pipe(takeUntil(this.unsubscribeAll));
  }

  get onStartChanged$(): Observable<number> {
    return this.startSubject.pipe(takeUntil(this.unsubscribeAll));
  }

  get onEndChanged$(): Observable<number> {
    return this.endSubject.pipe(takeUntil(this.unsubscribeAll));
  }

  get onPlayerChanged$(): Observable<{ currentTime: number }> {
    return this.playerChangedSubject.pipe(takeUntil(this.unsubscribeAll));
  }

  get onWaveformZoom$(): Observable<{ width: number; zoom: number }> {
    return this.waveformZoomSubject.pipe(takeUntil(this.unsubscribeAll));
  }

  get onPlayerLoaded$(): Observable<{ duration: number }> {
    return this.playerLoadedSubject.pipe(
      takeUntil(this.unsubscribeAll),
      // map(val => ({ duration: val.duration })),
    );
  }
}
